"use client";

import { useEffect, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { formatDateTime } from "@/lib/format-number";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  ADOPTION_REGIONS,
  type AdoptionRegion,
  calendarInUse,
  daysSkipped,
  doubleYear,
  firstGregorianRd,
  fixedFromJulian,
  gregorianJulianGap,
  isBeforeLadyDay,
  isValidJulianDate,
  julianFromFixed,
  julianMonthLength,
  lastJulianRd,
} from "@/lib/tool-kit/calendars/julian";
import {
  isoFromRd,
  parseGregorianIso,
  RD_LAST_GREGORIAN,
  rdFromGregorian,
  utcDateFromRd,
} from "@/lib/tool-kit/calendars/rd";
import type { CivilDate } from "@/lib/tool-kit/converters/week-number";

const GREGORIAN_REFORM_RD = rdFromGregorian(1582, 10, 15);
const FIRST_RD = 1;
const MAX_JULIAN_YEAR = 9999;
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/** English strings. A translated content file overrides these through `ui`. */
export const JULIAN_CALENDAR_CONVERTER_STRINGS = {
  direction: "Convert",
  toJulian: "Gregorian → Julian",
  toGregorian: "Julian → Gregorian",
  gregorianDate: "Gregorian date (New Style)",
  today: "Today",
  julianDay: "Day",
  julianMonth: "Month",
  julianYear: "Year",
  regionLabel: "Place and period to check",
  regionNone: "None — show the two calendars only",
  regions: {
    papalSpain: "Spain, Portugal, Papal States and most Italian states",
    france: "France",
    denmark: "Denmark–Norway and Protestant Germany",
    britain: "Great Britain, Ireland and the colonies",
    sweden: "Sweden and Finland",
    bulgaria: "Bulgaria",
    russia: "Russia (Soviet decree)",
    romania: "Romania",
    greece: "Greece",
  },
  julianLabel: "Julian date (Old Style)",
  gregorianLabel: "Gregorian date (New Style)",
  weekdayLabel: "Day of the week",
  isoLabel: "ISO 8601 (proleptic Gregorian)",
  gapLabel: "Gap between the calendars",
  gapValue: "one: {count} day|other: {count} days",
  julianDate: "{day} {month} {year}",
  dualDate: "{julian} O.S. / {gregorian} N.S.",
  dualDateLabel: "Dual date",
  inUseJulian:
    "{region} was still using the Julian calendar on this day, so a document written there is dated {julian}.",
  inUseGregorian:
    "{region} had already adopted the Gregorian calendar on this day, so a document written there is dated {gregorian}.",
  switchLine:
    "The switch: {last} (Julian) was followed by {first} (Gregorian), which dropped {skipped} from the count.",
  doubleYearLine:
    "Until 1751 the English year began on 25 March, so a document of this day carries the double year {year}: {date}.",
  proleptic:
    "Before 15 October 1582 nobody used the Gregorian calendar. The Gregorian date is proleptic — the modern calendar run backwards — and the Julian date is what contemporaries wrote.",
  pickDate: "Pick a date to convert.",
  invalidGregorian:
    "Choose a real date between 1 January 1 and 31 December 9999.",
  enterJulian: "Enter a Julian day, month and year.",
  invalidYear: "Enter a Julian year from 1 to 9999.",
  noSuchDay:
    "{month} {year} has only {length} days in the Julian calendar, so day {day} does not exist.",
  outsideRange: "That date falls outside 1 CE to 9999 CE.",
  tableHeading: "When countries switched to the Gregorian calendar",
  placeColumn: "Place",
  lastColumn: "Last Julian day",
  firstColumn: "First Gregorian day",
  skippedColumn: "Days dropped",
  regionsNote:
    "Many places switched in stages — German, Dutch and Swiss states, for example, changed between 1582 and 1812 — so check the particular town or state in your source.",
  copy: "Copy result",
  copied: "Copied!",
  copySummary: "{julian} O.S. = {gregorian} N.S.",
} as const;

type Strings = typeof JULIAN_CALENDAR_CONVERTER_STRINGS;
type Direction = "to-julian" | "to-gregorian";
type RegionId = "none" | AdoptionRegion["id"];

/** Today on the reader's own clock — only ever called after hydration. */
function todayRd(): number {
  const now = new Date();
  return rdFromGregorian(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

function useFormatters(strings: Strings) {
  const locale = useToolLocale();
  const monthName = (month: number) =>
    formatDateTime(locale, new Date(Date.UTC(2001, month - 1, 1)), {
      month: "long",
      timeZone: "UTC",
    });
  const gregorian = (rd: number) =>
    formatDateTime(locale, utcDateFromRd(rd), {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
  return {
    monthName,
    gregorian,
    weekday: (rd: number) =>
      formatDateTime(locale, utcDateFromRd(rd), {
        weekday: "long",
        timeZone: "UTC",
      }),
    julian: (date: CivilDate, yearText?: string) =>
      t(strings.julianDate, {
        day: String(date.day),
        month: monthName(date.month),
        year: yearText ?? String(date.year),
      }),
    julianFromRd: (rd: number) =>
      t(strings.julianDate, {
        day: String(julianFromFixed(rd).day),
        month: monthName(julianFromFixed(rd).month),
        year: String(julianFromFixed(rd).year),
      }),
  };
}

type Formatters = ReturnType<typeof useFormatters>;

function regionLines(
  region: AdoptionRegion,
  rd: number,
  strings: Strings,
  format: Formatters
): string[] {
  const name = strings.regions[region.id as keyof Strings["regions"]];
  const julian = julianFromFixed(rd);
  if (calendarInUse(region, rd) === "gregorian") {
    return [
      t(strings.inUseGregorian, {
        region: name,
        gregorian: format.gregorian(rd),
      }),
    ];
  }
  const lines = [
    t(strings.inUseJulian, { region: name, julian: format.julian(julian) }),
  ];
  if (region.ladyDayYear && isBeforeLadyDay(julian) && julian.year <= 1751) {
    const double = doubleYear(julian.year);
    lines.push(
      t(strings.doubleYearLine, {
        year: double,
        date: format.julian(julian, double),
      })
    );
  }
  return lines;
}

function ResultPanel({
  rd,
  direction,
  region,
  strings,
}: {
  rd: number;
  direction: Direction;
  region: AdoptionRegion | null;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const format = useFormatters(strings);
  const julian = julianFromFixed(rd);
  const julianText = format.julian(julian);
  const gregorianText = format.gregorian(rd);
  const gap = gregorianJulianGap(rd);
  const lines = region ? regionLines(region, rd, strings, format) : [];
  if (region) {
    lines.push(
      t(strings.switchLine, {
        last: format.julianFromRd(lastJulianRd(region)),
        first: format.gregorian(firstGregorianRd(region)),
        skipped: tPlural(strings.gapValue, locale, daysSkipped(region), {
          count: fmt(daysSkipped(region)),
        }),
      })
    );
  }
  if (rd < GREGORIAN_REFORM_RD) {
    lines.push(strings.proleptic);
  }
  return (
    <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>
        {direction === "to-julian" ? julianText : gregorianText}
      </h2>
      <StatGrid>
        <StatCard
          emphasis={direction === "to-julian"}
          label={strings.julianLabel}
          value={julianText}
        />
        <StatCard
          emphasis={direction === "to-gregorian"}
          label={strings.gregorianLabel}
          value={gregorianText}
        />
        <StatCard label={strings.weekdayLabel} value={format.weekday(rd)} />
        <StatCard
          label={strings.gapLabel}
          value={tPlural(strings.gapValue, locale, gap, { count: fmt(gap) })}
        />
        <StatCard label={strings.isoLabel} value={isoFromRd(rd)} />
      </StatGrid>
      <p className="text-foreground text-sm">
        <span className="font-medium">{strings.dualDateLabel}: </span>
        {t(strings.dualDate, { julian: julianText, gregorian: gregorianText })}
      </p>
      {lines.length > 0 ? (
        <ul className="space-y-1 text-foreground text-sm leading-relaxed">
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copySummary, {
          julian: julianText,
          gregorian: gregorianText,
        })}
      />
    </section>
  );
}

function AdoptionTable({ strings }: { strings: Strings }) {
  const fmt = useToolNumber();
  const format = useFormatters(strings);
  const rows = ADOPTION_REGIONS.map((region) => [
    strings.regions[region.id as keyof Strings["regions"]],
    format.julianFromRd(lastJulianRd(region)),
    format.gregorian(firstGregorianRd(region)),
    fmt(daysSkipped(region)),
  ]);
  return (
    <section className={`${CARD} space-y-3`}>
      <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
      <DataTable
        head={[
          strings.placeColumn,
          strings.lastColumn,
          strings.firstColumn,
          strings.skippedColumn,
        ]}
        rows={rows}
      />
      <p className="text-muted-foreground text-sm">{strings.regionsNote}</p>
    </section>
  );
}

interface JulianFields {
  day: string;
  month: string;
  year: string;
}

type Reading = { rd: number } | { error: string } | null;

function readGregorian(text: string, strings: Strings): Reading {
  if (text.trim() === "") {
    return null;
  }
  const rd = parseGregorianIso(text);
  return rd === null ? { error: strings.invalidGregorian } : { rd };
}

function readJulian(
  fields: JulianFields,
  strings: Strings,
  format: Formatters,
  fmt: (value: number) => string
): Reading {
  if (fields.day.trim() === "" || fields.year.trim() === "") {
    return null;
  }
  const day = parseIntegerInput(fields.day);
  const year = parseIntegerInput(fields.year);
  const month = Number(fields.month);
  if (year === null || year < 1 || year > MAX_JULIAN_YEAR) {
    return { error: strings.invalidYear };
  }
  if (day === null || day < 1 || day > 31) {
    return { error: strings.enterJulian };
  }
  if (!isValidJulianDate({ year, month, day })) {
    return {
      error: t(strings.noSuchDay, {
        month: format.monthName(month),
        year: String(year),
        length: fmt(julianMonthLength(year, month)),
        day: fmt(day),
      }),
    };
  }
  const rd = fixedFromJulian({ year, month, day });
  return rd < FIRST_RD || rd > RD_LAST_GREGORIAN
    ? { error: strings.outsideRange }
    : { rd };
}

export default function JulianCalendarConverterWidget() {
  const strings = useToolUi(JULIAN_CALENDAR_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const format = useFormatters(strings);
  const [direction, setDirection] = useState<Direction>("to-julian");
  const [gregorian, setGregorian] = useState("");
  const [regionId, setRegionId] = useState<RegionId>("none");
  const [fields, setFields] = useState<JulianFields>({
    day: "",
    month: "1",
    year: "",
  });

  useEffect(() => {
    const today = todayRd();
    const julian = julianFromFixed(today);
    setGregorian((current) => current || isoFromRd(today));
    setFields((current) =>
      current.year === ""
        ? {
            day: String(julian.day),
            month: String(julian.month),
            year: String(julian.year),
          }
        : current
    );
  }, []);

  const reading =
    direction === "to-julian"
      ? readGregorian(gregorian, strings)
      : readJulian(fields, strings, format, fmt);
  const region =
    ADOPTION_REGIONS.find((entry) => entry.id === regionId) ?? null;
  const setField = (key: keyof JulianFields) => (value: string) =>
    setFields((current) => ({ ...current, [key]: value }));

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.direction}
        name="jc-direction"
        onChange={setDirection}
        options={[
          { value: "to-julian", label: strings.toJulian },
          { value: "to-gregorian", label: strings.toGregorian },
        ]}
        value={direction}
      />
      {direction === "to-julian" ? (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <TextField
              id="jc-gregorian"
              label={strings.gregorianDate}
              onChange={setGregorian}
              type="date"
              value={gregorian}
            />
          </div>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setGregorian(isoFromRd(todayRd()))}
            type="button"
          >
            {strings.today}
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-[1fr_2fr_1fr]">
          <TextField
            id="jc-day"
            label={strings.julianDay}
            numeric
            onChange={setField("day")}
            value={fields.day}
          />
          <SelectField
            id="jc-month"
            label={strings.julianMonth}
            onChange={setField("month")}
            options={MONTHS.map((month) => ({
              value: String(month),
              label: format.monthName(month),
            }))}
            value={fields.month}
          />
          <TextField
            id="jc-year"
            label={strings.julianYear}
            numeric
            onChange={setField("year")}
            value={fields.year}
          />
        </div>
      )}
      <SelectField
        id="jc-region"
        label={strings.regionLabel}
        onChange={setRegionId}
        options={[
          { value: "none", label: strings.regionNone },
          ...ADOPTION_REGIONS.map((entry) => ({
            value: entry.id,
            label: strings.regions[entry.id as keyof Strings["regions"]],
          })),
        ]}
        value={regionId}
      />
      {reading === null ? (
        <Notice>
          {direction === "to-julian" ? strings.pickDate : strings.enterJulian}
        </Notice>
      ) : null}
      {reading !== null && "error" in reading ? (
        <ErrorNotice>{reading.error}</ErrorNotice>
      ) : null}
      {reading !== null && "rd" in reading ? (
        <ResultPanel
          direction={direction}
          rd={reading.rd}
          region={region}
          strings={strings}
        />
      ) : null}
      <AdoptionTable strings={strings} />
    </div>
  );
}
